import React, { useEffect, useState } from 'react';
import { AlertTriangle, Users, FileText, Car, Wrench, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { API } from '../lib/api';
import { statusBadge, FEATURES } from '../lib/brand';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentCases, setRecentCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [statsRes, casesRes] = await Promise.all([
          API.get('/admin/stats'),
          API.get('/admin/cases'),
        ]);
        setStats(statsRes.data);
        setRecentCases(casesRes.data.slice(0, 6));
      } catch (e) {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const tiles = stats ? [
    { label: 'Schadenfälle gesamt', value: stats.cases.total, icon: AlertTriangle, to: '/admin/schadenfaelle' },
    { label: 'Neu', value: stats.cases.neu, icon: AlertTriangle, to: '/admin/schadenfaelle?status=neu', accent: true },
    { label: 'In Bearbeitung', value: stats.cases.in_bearbeitung, icon: Wrench, to: '/admin/schadenfaelle?status=in_bearbeitung' },
    { label: 'In Reparatur', value: stats.cases.reparatur, icon: Wrench, to: '/admin/schadenfaelle?status=reparatur' },
    { label: 'Kunden', value: stats.customers, icon: Users, to: '/admin/kunden' },
    ...(FEATURES.towing_enabled ? [{ label: 'Ersatzfahrzeuge', value: `${stats.replacement_vehicles.available}/${stats.replacement_vehicles.total}`, icon: Car, to: '/admin/ersatzfahrzeuge' }] : []),
    { label: 'Dokumente', value: stats.documents, icon: FileText, to: '/admin/dokumente' },
    ...(FEATURES.pannenhilfe_enabled ? [{ label: 'Pannenhilfe', value: stats.breakdowns, icon: Wrench, to: '/admin/pannenhilfe' }] : []),
  ] : [];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="font-heading font-semibold text-2xl text-[hsl(var(--primary))]">Dashboard</h1>
        <p className="text-sm text-slate-600">Übersicht über alle Schadenfälle und Aktivitäten.</p>
      </div>

      {loading ? (
        <div className="text-sm text-slate-600">Lade...</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="admin-stats-grid">
            {tiles.map((t) => (
              <Link key={t.label} to={t.to} className="block">
                <Card className={`rounded-2xl border-slate-200 hover:uh-shadow-md transition-shadow ${t.accent ? 'ring-1 ring-[hsl(var(--accent))]' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="h-9 w-9 rounded-xl bg-sky-50 text-[hsl(var(--primary))] flex items-center justify-center"><t.icon className="h-4 w-4" /></div>
                      <ArrowRight className="h-4 w-4 text-slate-400" />
                    </div>
                    <div className="mt-3 text-xs text-slate-500">{t.label}</div>
                    <div className="font-heading font-semibold text-2xl text-[hsl(var(--primary))]">{t.value}</div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          <Card className="rounded-2xl border-slate-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="font-heading font-semibold text-slate-900">Aktuelle Schadenfälle</div>
                <Link to="/admin/schadenfaelle" className="text-sm text-[hsl(var(--primary))] font-medium">Alle anzeigen</Link>
              </div>
              {recentCases.length === 0 ? (
                <div className="text-sm text-slate-600">Noch keine Schadenfälle.</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {recentCases.map((c) => {
                    const s = statusBadge(c.status);
                    return (
                      <li key={c.id} className="py-3 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-mono text-xs text-slate-500">{c.case_number}</div>
                          <div className="font-medium truncate">{c.person.first_name} {c.person.last_name} — {c.vehicle.make} {c.vehicle.model}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${s.cls}`}>{s.label}</span>
                          <Link to={`/admin/schadenfaelle/${c.id}`} className="text-sm text-[hsl(var(--primary))] font-medium" data-testid={`admin-dashboard-case-${c.id}`}>Öffnen</Link>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
